import {
  hasMarketSettings,
  WarehousesRow,
} from '@/components/supplyItems/types/WarehouseRows';
import {
  InventoryCheckBehavior,
  StandingOrderBehavior,
} from '@/graphql/types/generated';

export type MarketOrderingSetting = {
  label: string;
  on: boolean;
  // Standing orders start off and the check starts on, so the mark alone cannot say which value
  // is worth noticing; colour marks a departure from where a market starts.
  isException: boolean;
  exceptionColor: string;
  description: string;
};

/**
 * The settings a market's Ordering cell shows, in order. Non-inventory items have no out-of-stock
 * check, so theirs is omitted.
 */
export const marketOrderingSettings = (
  standingOrderBehavior: StandingOrderBehavior,
  inventoryCheckBehavior?: InventoryCheckBehavior,
): MarketOrderingSetting[] => {
  const standingOrders =
    standingOrderBehavior === StandingOrderBehavior.Available;
  const settings: MarketOrderingSetting[] = [
    {
      label: 'Standing Orders',
      on: standingOrders,
      isException: standingOrders,
      exceptionColor: 'informationHover',
      description: standingOrders
        ? 'Standing orders enabled'
        : 'Standing orders not enabled',
    },
  ];
  if (inventoryCheckBehavior) {
    const skipped = inventoryCheckBehavior === InventoryCheckBehavior.Skip;
    settings.push({
      label: 'OOS Checks',
      on: !skipped,
      isException: skipped,
      exceptionColor: 'warningHover',
      description: skipped
        ? 'Out-of-stock checks skipped'
        : 'Out-of-stock checks enabled',
    });
  }
  return settings;
};

export const standingOrdersOn = (row: WarehousesRow) =>
  row.standingOrderBehavior === StandingOrderBehavior.Available;

export const oosCheckSkipped = (row: WarehousesRow) =>
  row.inventoryCheckBehavior === InventoryCheckBehavior.Skip;

/**
 * Where a setting is on, among the markets the product is sold in: "PDX1, SEA1 (2 of 3 markets)".
 * One definition so the editor and the read-only view count the same markets.
 */
export const marketsWhere = (
  rows: WarehousesRow[],
  applies: (row: WarehousesRow) => boolean,
) => {
  const markets = rows.filter(hasMarketSettings);
  const names = markets.filter(applies).map((row) => row.name);
  return `${names.length ? names.join(', ') : 'No markets'} (${
    names.length
  } of ${markets.length} markets)`;
};

/**
 * What saving a market's settings dialog changes, in the words its footer uses. Other markets only
 * ever receive the ordering settings, never Coming Soon.
 */
export const marketSettingsScope = (market: string, alsoApplyTo: string[]) => {
  const reach = alsoApplyTo.length
    ? `Ordering changes in ${[market, ...alsoApplyTo].join(', ')}.`
    : `Only ${market} changes.`;
  return `${reach} Takes effect when you update the item.`;
};
